import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Volume2, VolumeX } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import { SFX, BACKGROUNDS, SCAPES } from '../../audio/sfxManifest';

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SfxButton = styled.button`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #2a2a36;
  background: #16161f;
  color: #ccc;
  font-size: 14px;
  cursor: pointer;

  &:hover { background: #22222e; }
`;

const Select = styled.select`
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #2a2a36;
  background: #16161f;
  color: #ccc;
  font-size: 12px;
  max-width: 150px;
`;

const Slider = styled.input`
  width: 70px;
  accent-color: #2563eb;
`;

const MasterButton = styled.button`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$muted ? '#ef4444' : '#4caf50')};
  background: ${p => (p.$muted ? 'rgba(239,68,68,0.15)' : 'rgba(76,175,80,0.15)')};
  color: ${p => (p.$muted ? '#fca5a5' : '#8fd694')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
`;

const SFX_ICONS = { chime: '🔔', ding: '🛎️', tick: '⏱️', buzz: '⛔' };

/**
 * AudioControls — the facilitator's sound surface (PlayView control bar).
 * Left: one-shot SFX buttons (fire immediately at the audio-host screen).
 * Right: background channel — track select + volume, faded on the screens.
 * Background changes persist in flow audio_state, so they survive tila ticks
 * until a tila that declares its own audio overrides them.
 */
const AudioControls = ({ instanceId }) => {
  const [bgKey, setBgKey] = useState('');
  const [volume, setVolume] = useState(0.5);
  const [scape, setScape] = useState('');
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    fetch(API_ENDPOINTS.FLOW_STATE(instanceId))
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        const bg = data?.audioState?.background;
        if (bg?.key) { setBgKey(bg.key); setVolume(bg.volume ?? 0.5); }
        if (data?.audioState?.ambient?.scape) setScape(data.audioState.ambient.scape);
        setMuted(!!data?.audioState?.muted);
      })
      .catch(() => {});
  }, [instanceId]);

  const applyAmbient = (scapeKey) => {
    fetch(API_ENDPOINTS.FLOW_AUDIO(instanceId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ambient: scapeKey ? { scape: scapeKey } : null }),
    }).catch(() => {});
  };

  const fireSfx = (key) => {
    fetch(API_ENDPOINTS.FLOW_SFX(instanceId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key }),
    }).catch(() => {});
  };

  /** Master mute for everything Kuutio plays in the room — SFX, background,
   *  ambient and the video soundtrack. The walls' own <video> elements are
   *  already muted, so this is the whole of it. */
  const applyMuted = (next) => {
    setMuted(next);
    fetch(API_ENDPOINTS.FLOW_AUDIO(instanceId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ muted: next }),
    }).catch(() => {});
  };

  const applyBackground = (key, vol) => {
    fetch(API_ENDPOINTS.FLOW_AUDIO(instanceId), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ background: key ? { key, volume: vol } : null }),
    }).catch(() => {});
  };

  return (
    <Bar>
      <MasterButton
        $muted={muted}
        onClick={() => applyMuted(!muted)}
        title="Kaikki Kuution äänet salissa: tehosteet, taustaääni, tilaääni ja videoiden ääniraita. Ei vaikuta esikatseluun tällä koneella."
      >
        {muted ? '🔇 Äänet pois' : '🔊 Äänet päällä'}
      </MasterButton>

      {Object.entries(SFX).map(([key, clip]) => (
        <SfxButton key={key} title={clip.label} onClick={() => fireSfx(key)}>
          {SFX_ICONS[key] || '🔊'}
        </SfxButton>
      ))}

      {bgKey ? <Volume2 size={16} color="#4caf50" /> : <VolumeX size={16} color="#555" />}
      <Select
        value={bgKey}
        onChange={(e) => { setBgKey(e.target.value); applyBackground(e.target.value, volume); }}
        title="Taustaääni"
      >
        <option value="">Ei taustaääntä</option>
        {Object.entries(BACKGROUNDS).map(([key, track]) => (
          <option key={key} value={key}>{track.label}</option>
        ))}
      </Select>
      <Slider
        type="range"
        min="0" max="1" step="0.05"
        value={volume}
        disabled={!bgKey}
        onChange={(e) => setVolume(Number(e.target.value))}
        onMouseUp={() => applyBackground(bgKey, volume)}
        onTouchEnd={() => applyBackground(bgKey, volume)}
        title="Taustaäänen voimakkuus"
      />

      <Select
        value={scape}
        onChange={(e) => { setScape(e.target.value); applyAmbient(e.target.value); }}
        title="Tilaääni — jokainen näyttö soittaa oman raitansa"
      >
        <option value="">Ei tilaääntä</option>
        {Object.entries(SCAPES).map(([key, s]) => (
          <option key={key} value={key}>🌲 {s.label}</option>
        ))}
      </Select>
    </Bar>
  );
};

export default AudioControls;
